/**
 * Character Migration Service
 * Centralizes all character data migrations in one place.
 *
 * Schema Version History:
 * - v1 (implicit): Original schema, no versioning
 * - v2: Added levelHistory, traitMarks, permanentSlots (CharacterBuilder.js)
 * - v3: Normalized subclassFeatures, consolidated token init, equipped migration
 */

import { initializeCharacterTokens } from './SimpleTokenService';

export const CURRENT_SCHEMA_VERSION = 3;

/**
 * Get the spellcast trait for a character based on their subclass
 * @param {Object} character - Character data
 * @param {Object} allData - Game data with subclasses
 * @returns {string|null} Spellcast trait name or null
 */
export const getSpellcastTrait = (character, allData) => {
  if (!allData?.subclasses || !character?.subclass) return null;
  const subclass = allData.subclasses[character.subclass];
  return subclass?.spellcastTrait || null;
};

/**
 * Migrate levelHistory, traitMarks, permanentSlots (v1->v2)
 * @param {Object} character - Character data
 * @returns {Object} Updated character
 */
const migrateSchemaV1ToV2 = (character) => {
  let updated = { ...character };
  let changed = false;

  // levelHistory - reconstruct from current state
  if (!updated.levelHistory) {
    const levelHistory = {};
    for (let lvl = 1; lvl <= (updated.level || 1); lvl++) {
      levelHistory[lvl] = {
        choices: lvl === 1 ? {
          class: updated.class,
          subclass: updated.subclass,
          ancestry: updated.ancestry,
          community: updated.community,
          traits: updated.traits,
          equipment: updated.equipment,
          loadout: updated.loadout,
          experiences: updated.experiences,
          background: updated.background,
          pronouns: updated.pronouns
        } : {
          advancements: [],
          legacy: true // Flag as pre-migration
        },
        timestamp: updated.updatedAt || updated.createdAt
      };
    }
    updated.levelHistory = levelHistory;
    changed = true;
  }

  // traitMarks
  if (!updated.traitMarks) {
    updated.traitMarks = {};
    changed = true;
  }

  // permanentSlots
  if (!updated.permanentSlots) {
    updated.permanentSlots = { hp: [], stress: [] };
    changed = true;
  }

  return { character: updated, changed };
};

/**
 * Normalize subclassFeatures.foundation to array-of-strings format
 * Fixes bug where TabbedCharacterSheet stored objects instead of strings
 * Also backfills empty foundation arrays for legacy characters
 * @param {Object} character - Character data
 * @param {Object} allData - Game data with subclasses
 * @returns {Object} Updated character
 */
const normalizeSubclassFeatures = (character, allData) => {
  // Case 1: Already has subclassFeatures - check and normalize format
  if (character.subclassFeatures) {
    let foundation = character.subclassFeatures.foundation;
    let changed = false;
    let normalizedFoundation = foundation;

    // Case 1a: foundation is an object with name property (BUG from TabbedCharacterSheet)
    if (foundation && typeof foundation === 'object' && !Array.isArray(foundation)) {
      normalizedFoundation = foundation.name ? [foundation.name] : [];
      changed = true;
    }

    // Case 1b: foundation is an array - validate entries are strings
    if (Array.isArray(foundation)) {
      normalizedFoundation = foundation.map(f =>
        typeof f === 'object' && f?.name ? f.name : f
      ).filter(f => typeof f === 'string');

      // Check if normalization changed anything
      if (JSON.stringify(normalizedFoundation) !== JSON.stringify(foundation)) {
        changed = true;
      }
    }

    // Case 1c: foundation is empty but character has a subclass with Foundation features
    // Backfill the foundation features for legacy characters created before subclassFeatures tracking
    if ((!normalizedFoundation || normalizedFoundation.length === 0) &&
        character.subclass && allData?.subclasses) {
      const subclassData = allData.subclasses[character.subclass];
      if (subclassData?.features) {
        const foundationFeatures = subclassData.features
          .filter(feat => feat.type === 'Foundation')
          .map(feat => feat.name);

        if (foundationFeatures.length > 0) {
          normalizedFoundation = foundationFeatures;
          changed = true;
        }
      }
    }

    if (changed) {
      return {
        character: {
          ...character,
          subclassFeatures: {
            ...character.subclassFeatures,
            foundation: normalizedFoundation,
            // Ensure specializations and masteries arrays exist
            specializations: character.subclassFeatures.specializations || [],
            masteries: character.subclassFeatures.masteries || []
          }
        },
        changed: true
      };
    }

    // Already correct format
    return { character, changed: false };
  }

  // Case 2: No subclassFeatures - initialize from subclass data
  if (!character.subclass || !allData?.subclasses) {
    return { character, changed: false };
  }

  const subclassData = allData.subclasses[character.subclass];
  if (!subclassData?.features) {
    // No features data available, create empty structure
    return {
      character: {
        ...character,
        subclassFeatures: {
          foundation: [],
          specializations: [],
          masteries: []
        }
      },
      changed: true
    };
  }

  // Extract foundation features from subclass data
  const foundationFeatures = subclassData.features
    .filter(feat => feat.type === 'Foundation')
    .map(feat => feat.name);

  return {
    character: {
      ...character,
      subclassFeatures: {
        foundation: foundationFeatures,
        specializations: [],
        masteries: []
      }
    },
    changed: true
  };
};

/**
 * Migrate equipped field from old equipment structure
 * @param {Object} character - Character data
 * @returns {Object} Updated character
 */
const migrateEquippedField = (character) => {
  if (character.equipped) {
    return { character, changed: false };
  }

  return {
    character: {
      ...character,
      equipped: {
        primaryWeapon: character.equipment?.primaryWeapon || null,
        secondaryWeapon: character.equipment?.secondaryWeapon || null,
        armor: character.equipment?.armor || null
      }
    },
    changed: true
  };
};

/**
 * Add slayerDice array for warriors
 * @param {Object} character - Character data
 * @returns {Object} Updated character
 */
const migrateSlayerDice = (character) => {
  if (character.slayerDice !== undefined || character.class !== 'warrior') {
    return { character, changed: false };
  }

  return {
    character: {
      ...character,
      slayerDice: []
    },
    changed: true
  };
};

/**
 * Initialize tokens for character's domain cards
 * Only marks as "changed" if actual token VALUES changed (current/maximum),
 * not just metadata (description, refreshTrigger, etc.)
 * This prevents race conditions where migration saves overwrite user actions.
 * @param {Object} character - Character data
 * @param {Object} allData - Game data
 * @returns {Object} Updated character
 */
const migrateTokens = (character, allData) => {
  const spellcastTrait = getSpellcastTrait(character, allData);
  const updated = initializeCharacterTokens(character, spellcastTrait);

  // Only consider it "changed" if actual token values changed, not just metadata
  let valuesChanged = false;

  const beforeResources = character.cardResources || {};
  const afterResources = updated.cardResources || {};

  // Check if any resource categories were added
  const beforeKeys = Object.keys(beforeResources);
  const afterKeys = Object.keys(afterResources);

  for (const categoryKey of afterKeys) {
    const beforeCategory = beforeResources[categoryKey] || {};
    const afterCategory = afterResources[categoryKey] || {};

    // Check if this is a new category
    if (!beforeResources[categoryKey]) {
      valuesChanged = true;
      break;
    }

    // Check each token in this category
    for (const tokenId of Object.keys(afterCategory)) {
      const beforeToken = beforeCategory[tokenId];
      const afterToken = afterCategory[tokenId];

      // New token was added
      if (!beforeToken) {
        valuesChanged = true;
        break;
      }

      // Check if current or maximum values differ
      if (beforeToken.current !== afterToken.current ||
          beforeToken.maximum !== afterToken.maximum) {
        valuesChanged = true;
        break;
      }
    }

    if (valuesChanged) break;
  }

  // DEBUG: Log Channel Raw Power token state before/after
  if (character.class === 'sorcerer') {
    const beforeCRP = character.cardResources?.['class-sorcerer']?.['channel-raw-power'];
    const afterCRP = updated.cardResources?.['class-sorcerer']?.['channel-raw-power'];
    console.log('[migrateTokens] Channel Raw Power migration:', {
      beforeCurrent: beforeCRP?.current,
      afterCurrent: afterCRP?.current,
      valuesChanged,
      willSave: valuesChanged
    });
  }

  return { character: updated, changed: valuesChanged };
};

/**
 * Main migration function - applies all necessary migrations
 * @param {Object} character - Character data from Firestore
 * @param {Object} allData - Game data from DataContext
 * @returns {Object} { character: Object, needsSave: boolean, migrationsRun: string[] }
 */
export const migrateCharacter = (character, allData) => {
  if (!character) {
    return { character: null, needsSave: false, migrationsRun: [] };
  }

  const currentVersion = character.schemaVersion || 1;
  const migrationsRun = [];

  // Already at current version - still run certain migrations for consistency
  // but don't bump schema version
  if (currentVersion >= CURRENT_SCHEMA_VERSION) {
    let updated = { ...character };
    const migrations = [];

    // Backfill subclass foundation features for legacy characters
    const subclassResult = normalizeSubclassFeatures(updated, allData);
    updated = subclassResult.character;
    if (subclassResult.changed) {
      migrations.push('backfillFoundationFeatures');
    }

    // Ensure tokens are initialized
    const tokenResult = migrateTokens(updated, allData);
    updated = tokenResult.character;
    if (tokenResult.changed) {
      migrations.push('tokenRefresh');
    }

    if (migrations.length > 0) {
      return {
        character: updated,
        needsSave: true,
        migrationsRun: migrations
      };
    }
    return { character, needsSave: false, migrationsRun: [] };
  }

  let updated = { ...character };

  // Schema v1 -> v2 migrations
  if (currentVersion < 2) {
    const result = migrateSchemaV1ToV2(updated);
    updated = result.character;
    if (result.changed) {
      migrationsRun.push('schemaV1ToV2');
    }
  }

  // Normalize subclassFeatures (fixes TabbedCharacterSheet bug)
  const subclassResult = normalizeSubclassFeatures(updated, allData);
  updated = subclassResult.character;
  if (subclassResult.changed) {
    migrationsRun.push('normalizeSubclassFeatures');
  }

  // Initialize tokens (handles legacy data, refreshTrigger backfill)
  const tokenResult = migrateTokens(updated, allData);
  updated = tokenResult.character;
  if (tokenResult.changed) {
    migrationsRun.push('initializeTokens');
  }

  // Slayer dice for warriors
  const slayerResult = migrateSlayerDice(updated);
  updated = slayerResult.character;
  if (slayerResult.changed) {
    migrationsRun.push('slayerDice');
  }

  // Equipped field
  const equippedResult = migrateEquippedField(updated);
  updated = equippedResult.character;
  if (equippedResult.changed) {
    migrationsRun.push('equippedField');
  }

  // Set schema version
  updated.schemaVersion = CURRENT_SCHEMA_VERSION;
  migrationsRun.push(`upgradeToV${CURRENT_SCHEMA_VERSION}`);

  return {
    character: updated,
    needsSave: migrationsRun.length > 0,
    migrationsRun
  };
};
